import React from "react";
import { useHistory } from "react-router-dom";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import {
  ActionButton,
  B2BSpinner,
  Icon,
  IconName,
  LoadingIndicator,
} from "halifax";
import {
  CallState,
  ExperiencePricingCategory,
  ExperiencesAgeBandEnum,
  ExperiencesPriceBreakdown,
  RewardsAccount,
} from "redmond";

import styles from "./styles.module.scss";
import { PATH_BOOK } from "../../../../utils/paths";
import { ExperiencesPricingBreakdown } from "../../../common";
import { IShopCancellationPolicyProps } from "../ShopCancellationPolicy/component";
import {
  CART_UPDATE_TEXT,
  CONTINUE_TO_CHECKOUT,
  NO_PRICE_BREAKDOWN_SUBTITLE,
  NO_PRICE_BREAKDOWN_TITLE,
} from "../../textConstants";
import { ShopCheckoutBreakdownSummary } from "../ShopCheckoutBreakdownSummary/component";
import { ParsedAgeBand } from "../../reducer";

export interface IShopCheckoutBreakdown {
  priceBreakdown?: ExperiencesPriceBreakdown;
  shopOptionCallState?: CallState;
  experienceMaxTravelers?: number;
  isFreeCancellation?: boolean;
  pricingCategory?: ExperiencePricingCategory;
  isLoading?: boolean;
}

export interface IShopCheckoutBreakdownProps extends IShopCheckoutBreakdown {
  parsedAgeBands?: { [key in ExperiencesAgeBandEnum]?: ParsedAgeBand };
  largestValueAccount?: RewardsAccount;
  cancellationPolicyProps: IShopCancellationPolicyProps | null;
  mobile?: boolean;
  disableCheckout?: boolean;
  showSummary?: boolean;
  noAvailability?: boolean;
}

export const ShopCheckoutBreakdown = (props: IShopCheckoutBreakdownProps) => {
  const history = useHistory();
  const {
    isLoading = true,
    priceBreakdown,
    shopOptionCallState,
    largestValueAccount,
    cancellationPolicyProps,
    isFreeCancellation = false,
    mobile = false,
    disableCheckout = false,
    showSummary = true,
    noAvailability,
  } = props;

  const renderNoPriceBreakdown = () => (
    <Box className={styles["experiences-no-price-breakdown-container"]}>
      <Icon name={IconName.ErrorState} />
      <Typography
        className={styles["experiences-no-price-breakdown-title"]}
        variant="h2"
      >
        {NO_PRICE_BREAKDOWN_TITLE}
      </Typography>
      <Typography className={styles["experiences-no-price-breakdown-subtitle"]}>
        {NO_PRICE_BREAKDOWN_SUBTITLE}
      </Typography>
    </Box>
  );

  return (
    <Box
      className={clsx(
        styles["experiences-shop-checkout-breakdown-container"],
        "experiences-shop-checkout-breakdown-container"
      )}
    >
      <Box
        className={clsx(
          styles["checkout-breakdown-section"],
          "checkout-breakdown-section"
        )}
      >
        {showSummary && <ShopCheckoutBreakdownSummary {...props} />}
        {isLoading && (
          <Box
            className={styles["experiences-price-breakdown-loading-wrapper"]}
          >
            <LoadingIndicator
              className={
                styles["experiences-price-breakdown-loading-indicator"]
              }
              indicatorSize="small"
              indicator={B2BSpinner}
              message={CART_UPDATE_TEXT}
            />
          </Box>
        )}
        {!isLoading &&
        ((shopOptionCallState === CallState.Success && noAvailability) ||
          shopOptionCallState === CallState.Failed) ? (
          renderNoPriceBreakdown()
        ) : (
          <ExperiencesPricingBreakdown
            className={clsx(styles["experiences-price-breakdown"])}
            priceBreakdown={priceBreakdown}
            largestValueAccount={largestValueAccount}
            isFreeCancellation={isFreeCancellation}
            cancellationPolicyProps={cancellationPolicyProps}
            mobile={mobile}
          />
        )}
        {!mobile && (
          <ActionButton
            className={styles["continue-button"]}
            message={CONTINUE_TO_CHECKOUT}
            onClick={() =>
              history.push(`${PATH_BOOK}${history.location.search}`)
            }
            disabled={disableCheckout}
          />
        )}
      </Box>
    </Box>
  );
};
